import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { IconThumbUp, IconThumbDown, IconX } from '@tabler/icons-react';
import { sendPageVerdict, useCurrentPageFeedbackSubview } from '@/lib/pageFeedback';

/**
 * SendFeedbackDialog: explicit page feedback, opened ONLY from the profile menu
 * ("Send feedback" in GarageHeaderProfessional). Doug LOCK 2026-10-03: no page
 * verdict pill, nudge, toast, banner or auto-shown prompt anywhere. This
 * renders nothing until the user opens it on purpose.
 *
 * It posts the same page_verdict as before through the existing app-feedback
 * edge function, so the DB still routes it to the page's Brain node owner seat
 * (docs/architecture/PAGE_FEEDBACK_ROUTING.md). The confirmation is inline in
 * the dialog the user opened (no toast).
 */
export default function SendFeedbackDialog({ open, onClose }) {
  const location = useLocation();
  const subview = useCurrentPageFeedbackSubview(location.pathname);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!open) {
      setNote('');
      setStatus(null);
      return undefined;
    }
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (verdict) => {
    if (sending) return;
    setSending(true);
    setStatus(null);
    try {
      const res = await sendPageVerdict({
        verdict,
        screen: location.pathname,
        subview,
        note: note.trim() || null,
      });
      const label = verdict === 'works' ? 'Works' : 'Needs work';
      setStatus({ ok: true, text: res.routed_seat_name ? `${label} sent to ${res.routed_seat_name}` : `${label} sent` });
      setNote('');
    } catch (err) {
      setStatus({ ok: false, text: `Not sent: ${err?.message || 'network error'}` });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="send-feedback-title"
      data-testid="send-feedback-dialog"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-xl border border-vegas-gold/30 bg-gray-950 p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between">
          <h2 id="send-feedback-title" className="text-sm font-semibold text-white">Send feedback about this page</h2>
          <button type="button" onClick={onClose} className="p-1 text-gray-500 hover:text-gray-300" aria-label="Close feedback">
            <IconX size={14} />
          </button>
        </div>
        <label htmlFor="send-feedback-note" className="mb-1 block text-[11px] text-gray-400">
          What works or needs work? (optional)
        </label>
        <textarea
          id="send-feedback-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
          maxLength={2000}
          autoFocus
          className="w-full resize-none rounded border border-gray-800 bg-gray-900 p-2 text-xs text-gray-100 focus:border-vegas-gold focus:outline-none"
        />
        {status && (
          <p role="status" className={`mt-2 text-[11px] ${status.ok ? 'text-emerald-300' : 'text-red-300'}`}>
            {status.text}
          </p>
        )}
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            disabled={sending}
            onClick={() => submit('works')}
            className="inline-flex items-center gap-1 rounded px-3 py-1.5 text-xs text-gray-200 hover:bg-emerald-500/15 hover:text-emerald-300 disabled:opacity-60"
          >
            <IconThumbUp size={13} /> Works
          </button>
          <button
            type="button"
            disabled={sending}
            onClick={() => submit('needs_work')}
            className="inline-flex items-center gap-1 rounded bg-vegas-gold px-3 py-1.5 text-xs font-semibold text-gray-950 disabled:opacity-60"
          >
            <IconThumbDown size={13} /> {sending ? 'Sending…' : 'Needs work'}
          </button>
        </div>
      </div>
    </div>
  );
}
